import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Sparkles, Gift, Users, MapPin, Phone, Star, Trophy, Heart, BookOpen } from 'lucide-react';

// SADA opening date — anniversary reference
const SADA_OPENING_DATE = new Date('2024-03-25');
// Anniversary week: March 22 - March 28 (matches SADANNIVERSAIRE schedule)
const ANNIV_START_MONTH = 2; // March (0-indexed)
const ANNIV_START_DAY = 22;
const ANNIV_END_MONTH = 2;
const ANNIV_END_DAY = 28;

function isInAnniversaryWeek(today = new Date()) {
  // Admin preview mode: ?preview_anniv=1 in URL
  try {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('preview_anniv') === '1') return true;
    }
  } catch {}
  const m = today.getMonth();
  const d = today.getDate();
  if (m < ANNIV_START_MONTH || m > ANNIV_END_MONTH) return false;
  if (m === ANNIV_START_MONTH && d < ANNIV_START_DAY) return false;
  if (m === ANNIV_END_MONTH && d > ANNIV_END_DAY) return false;
  return true;
}

function nextAnniversaryDate(today = new Date()) {
  const y = today.getFullYear();
  const thisYear = new Date(y, 2, 25);
  return today <= thisYear ? thisYear : new Date(y + 1, 2, 25);
}

function formatDuration(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return { d, h, m, s };
}

const VALUES = [
  { icon: Star, label: 'Qualité' },
  { icon: Heart, label: 'Éthique' },
  { icon: Users, label: 'Accessibilité' },
  { icon: Trophy, label: 'Professionnalisme' },
];

const PARTNERS = [
  'MUGEF-CI', 'WILLIS TOWERS', 'ASCOMA', 'MCI CARE', 'ALLIANZ', 'SUNU',
  'NSIA', 'CNPS', 'HENNER', 'SODECI', 'CIE', 'CIMEF', 'OLEA', 'BIAM',
  'AMSA', 'MUPEMENET', 'MADGI', 'ANKARA',
];

const AnniversarySection = ({ onNavigate }) => {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const inWeek = isInAnniversaryWeek(now);
  const target = inWeek
    ? new Date(now.getFullYear(), 2, 28, 23, 59, 59) // end of anniversary week
    : nextAnniversaryDate(now);
  const { d, h, m, s } = formatDuration(target - now);

  // Compute anniversary number dynamically (e.g., 2026 - 2024 = 2ᵉ anniversaire)
  const referenceAnniv = inWeek ? now : target;
  const years = Math.max(1, referenceAnniv.getFullYear() - SADA_OPENING_DATE.getFullYear());
  const ordinal = years === 1 ? '1ᵉʳ' : `${years}ᵉ`;

  const title = inWeek ? `🎉 ${ordinal} Anniversaire` : 'Prochainement';
  const subtitle = inWeek
    ? 'Merci pour votre confiance — célébrons ensemble'
    : `Notre ${ordinal} anniversaire approche`;

  return (
    <section className="max-w-7xl mx-auto px-4 py-8" data-testid="anniversary-section">
      {/* Hero banner */}
      <Card className="relative overflow-hidden border-0 shadow-2xl bg-gradient-to-br from-[#FFF8E7] via-[#FFF3D1] to-[#FFE5B4]">
        {/* Confetti decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-4 left-8 w-2 h-2 bg-[#D4AF37] rotate-12" />
          <div className="absolute top-12 right-24 w-3 h-3 bg-[#C45C26] rotate-45" />
          <div className="absolute bottom-16 left-32 w-2 h-6 bg-[#2E7D32] -rotate-12" />
          <div className="absolute top-24 right-12 w-4 h-1 bg-[#D4AF37] rotate-45" />
          <div className="absolute bottom-8 right-40 w-2 h-2 bg-[#C45C26]" />
          <div className="absolute top-1/3 left-1/4 w-1 h-5 bg-[#FFD700] rotate-12" />
        </div>

        <CardContent className="relative p-6 md:p-10">
          <div className="flex flex-col items-center text-center">
            <Badge className="bg-gradient-to-r from-[#D4AF37] to-[#C45C26] text-white border-0 text-xs uppercase tracking-widest px-4 py-1.5 shadow-lg">
              <Sparkles className="w-3 h-3 mr-1.5" />
              SADANNIVERSAIRE
            </Badge>
            <h2 className="text-4xl md:text-6xl font-black mt-4 bg-gradient-to-r from-[#C45C26] via-[#D4AF37] to-[#C45C26] bg-clip-text text-transparent leading-tight">
              {title}
            </h2>
            <p className="text-lg md:text-xl text-[#5D4E37] mt-2 font-medium">{subtitle}</p>
            <p className="text-sm text-[#8B7355] mt-1">
              Ouverte le 25 mars 2024 · Cocody II Plateaux, Abidjan
            </p>

            {/* Countdown */}
            <div className="mt-6 grid grid-cols-4 gap-3 md:gap-6">
              {[
                { v: d, l: 'Jours' },
                { v: h, l: 'Heures' },
                { v: m, l: 'Min' },
                { v: s, l: 'Sec' },
              ].map((c, i) => (
                <div
                  key={i}
                  className="bg-white/80 backdrop-blur border-2 border-[#D4AF37]/30 rounded-2xl px-4 py-3 md:px-6 md:py-4 shadow-lg"
                >
                  <div className="text-3xl md:text-4xl font-black text-[#C45C26] tabular-nums">
                    {String(c.v).padStart(2, '0')}
                  </div>
                  <div className="text-[10px] md:text-xs uppercase tracking-wider text-[#8B7355] font-semibold">
                    {c.l}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#8B7355] mt-3 italic">
              {inWeek ? 'Jusqu\'à la fin de la semaine anniversaire' : 'Jusqu\'au 25 mars, jour J'}
            </p>

            {/* Slogan */}
            <blockquote className="mt-6 md:mt-8 max-w-2xl italic text-[#5D4E37] text-base md:text-lg border-l-4 border-[#D4AF37] pl-4 py-2 bg-white/50 rounded-r-lg">
              « En marche vers une pharmacie de référence ;
              <span className="font-bold text-[#C45C26]"> je m'engage !</span> »
              <footer className="text-xs text-[#8B7355] mt-1 not-italic">— L'équipe SADA</footer>
            </blockquote>

            {/* Story - Notre Histoire */}
            <div className="mt-8 md:mt-10 max-w-3xl w-full bg-white/60 backdrop-blur rounded-2xl border-2 border-[#D4AF37]/30 shadow-lg overflow-hidden">
              <div className="bg-gradient-to-r from-[#D4AF37]/20 via-[#C45C26]/10 to-[#D4AF37]/20 px-5 py-3 border-b border-[#D4AF37]/30">
                <h3 className="flex items-center justify-center gap-2 text-[#5D4E37] font-bold text-lg">
                  <BookOpen className="w-5 h-5 text-[#C45C26]" />
                  Notre Histoire
                </h3>
              </div>
              <div className="p-5 md:p-6 text-left text-[#5D4E37] text-sm md:text-[15px] leading-relaxed space-y-3">
                <p>
                  <span className="inline-block bg-[#D4AF37] text-white text-xs font-black rounded-full px-2.5 py-0.5 align-middle mr-2">1995</span>
                  Il était une fois au tronc commun, <strong className="text-[#C45C26]">Ange</strong>, un aîné qui reprenait pour la seconde et dernière fois son année d'entrée en faculté de médecine. Il avait pris sous son aile deux petites étudiantes, <strong>Dr Irie Nathalie</strong> et <strong>Bah Rita</strong>, à qui il a donné les trucs et astuces pour réussir cet examen très sélectif de passage en année supérieure.
                </p>
                <p>
                  Malheureusement, Ange n'a pas pu réaliser son rêve d'être parmi les 10 % d'étudiants admis, et les deux étudiantes ont totalement perdu sa trace. Pour lui rendre hommage, Dr Irie a décidé, cette même année 1995, que <em className="text-[#C45C26]">le jour où elle aurait une pharmacie, elle la dénommerait</em> <strong>Pharmacie Saint Ange Des Amis</strong>.
                </p>
                <p>
                  D'autres amis ont depuis contribué — par leurs conseils, leurs appuis matériel, spirituel, financier et moral — à franchir les diverses étapes : obtenir le diplôme de docteur en pharmacie, l'autorisation d'ouvrir une officine, identifier le site, la licence d'exploitation, mobiliser les ressources auprès des banques, choisir les prestataires pour les travaux, recruter l'équipe… Tous ces amis sont ainsi honorés à travers la pharmacie et sauront chacun se reconnaître.
                </p>
                <p className="pt-2 border-t border-[#D4AF37]/20 italic text-[#8B7355]">
                  Enfin, pour toujours conserver la relation avec Dieu par qui et pour qui tout est fait, nous avons choisi de rendre <strong className="text-[#C45C26] not-italic">Saint</strong> le nom de la pharmacie et de la consacrer au Dieu pourvoyeur.
                </p>
                <div className="flex items-center justify-center gap-2 pt-3 text-xs text-[#8B7355]">
                  <span className="h-px w-12 bg-[#D4AF37]/40" />
                  <span className="uppercase tracking-widest font-semibold">
                    1995 · Une vocation · 2024 · Une pharmacie
                  </span>
                  <span className="h-px w-12 bg-[#D4AF37]/40" />
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3 mt-8 justify-center">
              <button
                onClick={() => onNavigate?.('reviews')}
                className="inline-flex items-center gap-2 bg-[#C45C26] hover:bg-[#A84B1F] text-white font-bold px-5 py-3 rounded-full shadow-lg transition-all hover:scale-105"
                data-testid="anniv-review-btn"
              >
                <Star className="w-4 h-4" />
                Laissez-nous un avis
              </button>
              <button
                onClick={() => onNavigate?.('gallery')}
                className="inline-flex items-center gap-2 bg-white text-[#5D4E37] hover:bg-[#FFF8E7] font-bold px-5 py-3 rounded-full border-2 border-[#D4AF37] shadow-lg transition-all hover:scale-105"
                data-testid="anniv-gallery-btn"
              >
                <Gift className="w-4 h-4 text-[#D4AF37]" />
                Notre photothèque
              </button>
              <button
                onClick={() => onNavigate?.('contact')}
                className="inline-flex items-center gap-2 bg-[#2E7D32] hover:bg-[#1B5E20] text-white font-bold px-5 py-3 rounded-full shadow-lg transition-all hover:scale-105"
                data-testid="anniv-referral-btn"
              >
                <Users className="w-4 h-4" />
                Parrainez un ami
              </button>
            </div>
          </div>

          {/* Values strip */}
          <div className="mt-10 pt-6 border-t border-[#D4AF37]/30">
            <p className="text-xs uppercase tracking-widest text-[#8B7355] text-center font-semibold mb-4">
              Nos engagements
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {VALUES.map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 bg-white/70 backdrop-blur rounded-xl px-3 py-2.5 border border-[#D4AF37]/20"
                >
                  <Icon className="w-4 h-4 text-[#C45C26]" />
                  <span className="text-sm font-semibold text-[#5D4E37]">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Trust badges */}
          <div className="mt-8">
            <p className="text-xs uppercase tracking-widest text-[#8B7355] text-center font-semibold mb-3">
              Ils nous font confiance depuis {years} an{years > 1 ? 's' : ''}
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              {PARTNERS.slice(0, 12).map((name) => (
                <span
                  key={name}
                  className="text-[11px] font-semibold text-[#5D4E37] bg-white/70 backdrop-blur border border-[#D4AF37]/30 rounded-full px-3 py-1"
                >
                  {name}
                </span>
              ))}
              <span className="text-[11px] font-semibold text-[#C45C26] bg-white/70 backdrop-blur border border-[#C45C26]/40 rounded-full px-3 py-1">
                + {PARTNERS.length - 12} partenaires
              </span>
            </div>
          </div>

          {/* Key policy */}
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            {['Zéro rupture', 'Célérité', 'Service personnalisé'].map((p) => (
              <Badge
                key={p}
                className="bg-gradient-to-r from-[#C45C26] to-[#D4AF37] text-white border-0 text-xs px-3 py-1.5 shadow-md"
              >
                ✓ {p}
              </Badge>
            ))}
          </div>

          {/* Footer contact strip */}
          <div className="mt-8 pt-6 border-t border-[#D4AF37]/30 grid md:grid-cols-3 gap-3 text-sm text-[#5D4E37]">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#C45C26] shrink-0" />
              <span>Cocody II Plateaux 7e Tranche — Carrefour Bluetooth</span>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-[#C45C26] shrink-0" />
              <a href="tel:+2250778415277" className="hover:text-[#C45C26]">07 78 41 52 77</a>
              <span className="text-[#8B7355]">·</span>
              <a href="tel:+2250769354024" className="hover:text-[#C45C26]">07 69 35 40 24</a>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[#8B7355] font-medium">Lun-Ven 7h30-20h · Sam 7h30-13h</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </section>
  );
};

export default AnniversarySection;
export { isInAnniversaryWeek };
